import { useState } from 'react'
import * as Yup from 'yup'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import { Eye, EyeOff, Loader2, AlertCircle, ArrowLeft, Lock, ShieldCheck, CheckCircle2 } from 'lucide-react'
import { requestPasswordReset } from '../core/_requests'
import axios from 'axios'

interface ResetPasswordFormValues {
  newPassword: string
  confirmPassword: string
}

export function ResetPassword() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const resetPasswordSchema = Yup.object().shape({
    newPassword: Yup.string()
      .min(6, 'Minimum 6 characters')
      .max(50, 'Maximum 50 characters')
      .required('New Password is required'),
    confirmPassword: Yup.string()
      .oneOf([Yup.ref('newPassword')], 'Passwords must match')
      .required('Confirm Password is required'),
  })

  const formik = useFormik<ResetPasswordFormValues>({
    initialValues: { newPassword: '', confirmPassword: '' },
    validationSchema: resetPasswordSchema,
    onSubmit: async (values, { setStatus }) => {
      setLoading(true)
      setErrorMsg('')
      try {
        await requestPasswordReset(token, values.newPassword)
        setSuccess(true)
      } catch (error: any) {
        let message = 'Something went wrong. Please try again.'
        if (axios.isAxiosError(error)) {
          if (error.response?.data?.message) {
            message = error.response.data.message
          }
        }
        setErrorMsg(message)
        setStatus(message)
      } finally {
        setLoading(false)
      }
    },
  })

  // No token in URL — show error
  if (!token) {
    return (
      <div>
        <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 mb-5">
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to sign in
        </Link>

        <div className="flex flex-col items-center text-center pt-6">
          <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mb-5">
            <AlertCircle className="w-7 h-7 text-red-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Invalid reset link</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-xs">
            This password reset link is missing or invalid. Please request a new one.
          </p>
        </div>

        <div className="mt-8 text-center">
          <Link
            to="/auth/forgot-password"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors"
          >
            Request new link
          </Link>
        </div>
      </div>
    )
  }

  // Success state
  if (success) {
    return (
      <div>
        <div className="flex flex-col items-center text-center pt-6">
          <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mb-5">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Password reset!</h2>
          <p className="mt-2 text-sm text-slate-500 max-w-xs">
            Your password has been changed successfully. You can now sign in with your new password.
          </p>
        </div>

        <div className="mt-8 text-center">
          <button
            type="button"
            onClick={() => navigate('/auth/login')}
            className="inline-flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors"
          >
            Sign in
          </button>
        </div>

        <p className="mt-7 text-center text-sm text-slate-600">
          Remember your password?{' '}
          <Link to="/auth/login" className="text-blue-600 font-semibold hover:underline">
            Sign in
          </Link>
        </p>
      </div>
    )
  }

  return (
    <form
      onSubmit={formik.handleSubmit}
      noValidate
      id="kt_reset_password_form"
    >
      <Link to="/auth/login" className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-800 mb-5">
        <ArrowLeft className="w-3.5 h-3.5" />
        Back to sign in
      </Link>

      <h2 className="text-2xl font-bold text-slate-900">Reset your password</h2>
      <p className="mt-1 text-sm text-slate-500">Enter a new password for your account.</p>

      <div className="mt-7 space-y-4">
        {/* Error display */}
        {errorMsg && (
          <div
            role="alert"
            className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-red-200 bg-red-50 text-xs text-red-700"
          >
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>{errorMsg}</span>
          </div>
        )}

        {/* New Password */}
        <div>
          <label htmlFor="newPassword" className="block text-xs font-medium text-slate-700 mb-1.5">
            New Password
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              placeholder="Enter new password"
              className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              {...formik.getFieldProps('newPassword')}
            />
            <button
              type="button"
              aria-label={showNewPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowNewPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formik.touched.newPassword && formik.errors.newPassword && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.newPassword}</p>
          )}
        </div>

        {/* Confirm Password */}
        <div>
          <label htmlFor="confirmPassword" className="block text-xs font-medium text-slate-700 mb-1.5">
            Confirm Password
          </label>
          <div className="relative">
            <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              id="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              required
              autoComplete="new-password"
              placeholder="Confirm new password"
              className="w-full pl-9 pr-10 py-2.5 text-sm rounded-lg border border-slate-200 bg-white shadow-sm placeholder:text-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
              {...formik.getFieldProps('confirmPassword')}
            />
            <button
              type="button"
              aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowConfirmPassword((v) => !v)}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100"
            >
              {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {formik.touched.confirmPassword && formik.errors.confirmPassword && (
            <p className="mt-1 text-xs text-red-600">{formik.errors.confirmPassword}</p>
          )}
        </div>

        {/* Submit */}
        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
          {loading ? 'Resetting…' : 'Reset Password'}
        </button>
      </div>

      <p className="mt-7 text-center text-sm text-slate-600">
        Remember your password?{' '}
        <Link to="/auth/login" className="text-blue-600 font-semibold hover:underline">
          Sign in
        </Link>
      </p>
    </form>
  )
}
